import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Users, ArrowRight, Hourglass } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/Bits";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { LogoImg } from "@/components/LogoImg";

export const INVITE_DEFAULT = "Pronostics NBA entre collègues : un classement, des défis amicaux, aucun pari, aucun argent.";

export const inkOn = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#0B0F0B" : "#FFFFFF";
};

// Couleurs de la page d'invitation, en style inline pour ne pas subir le mode Jour/Nuit.
export function inviteColors(brand) {
  const th = brand.invite_theme || {};
  const light = th.bg === "light";
  const button = th.button || brand.primary;
  return {
    light, page: light ? "#F4F3EF" : "#0B0F0B", card: light ? "rgba(255,255,255,0.94)" : "rgba(18,23,18,0.9)",
    line: light ? "rgba(11,15,11,0.1)" : "rgba(255,255,255,0.1)", text: th.text || (light ? "#0B0F0B" : "#FFFFFF"),
    muted: light ? "#4A4D45" : "#CBD5E1", soft: light ? "#6A6C63" : "#94A3B8", accent: th.accent || brand.secondary,
    button, buttonText: th.button_text || inkOn(button), chip: light ? "rgba(11,15,11,0.05)" : "rgba(255,255,255,0.05)",
  };
}

// Fond de la page d'invitation : la photo de fond de la ligue (si choisie) sous un voile noir ou blanc.
export function InviteBackdrop({ brand }) {
  const c = inviteColors(brand);
  const img = brand.bg_mode === "image" && brand.bg_url ? `url(${process.env.REACT_APP_BACKEND_URL}${brand.bg_url})` : null;
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10" style={{ background: `radial-gradient(circle at 20% 10%, ${brand.primary}${c.light ? "33" : "55"}, transparent 55%), ${c.page}` }} data-testid="invite-backdrop">
      {img && <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: img }} />}
      {img && <div className="absolute inset-0" style={{ background: c.light ? "rgba(244,243,239,0.82)" : "rgba(11,15,11,0.72)" }} />}
    </div>
  );
}

export function InviteCard({ d, testPrefix = "corp" }) {
  const { primary, secondary, logo_url: logo } = d.brand;
  const c = inviteColors(d.brand);
  return (
    <div className="w-full max-w-md overflow-hidden rounded-3xl border shadow-2xl backdrop-blur-xl" style={{ background: c.card, borderColor: c.line }} data-testid={`${testPrefix}-card`}>
      <div className="h-2" style={{ background: `linear-gradient(90deg, ${primary} 65%, ${secondary} 65%)` }} />
      <div className="space-y-5 p-7">
        {logo && <LogoImg src={`${process.env.REACT_APP_BACKEND_URL}${logo}`} fit={d.brand.logo_fit} alt={`Logo ${d.name}`} className="h-20 w-20 rounded-2xl" testId={`${testPrefix}-logo`} />}
        <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: c.accent }}>Ligue privée · Ma Petite Ligue</p>
        <h1 className="break-words font-display text-4xl font-extrabold uppercase leading-none sm:text-5xl" style={{ color: c.text }} data-testid={`${testPrefix}-name`}>{d.name} <VerifiedBadge show={d.brand.verified} size={30} /></h1>
        {d.brand.tagline && <p className="text-sm font-semibold" style={{ color: c.text }} data-testid={`${testPrefix}-tagline`}>{d.brand.tagline}</p>}
        <p className="whitespace-pre-line text-sm" style={{ color: c.muted }} data-testid={`${testPrefix}-invite-text`}>{d.brand.invite_text || INVITE_DEFAULT}</p>
        <p className="flex items-center gap-2 text-xs" style={{ color: c.soft }} data-testid={`${testPrefix}-count`}><Users size={14} /> {d.member_count}{d.max_members ? `/${d.max_members}` : ""} participants</p>
        {d.ready ? (
          <Link to={`/invitation/${d.token}`} className="flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-extrabold uppercase transition-transform hover:-translate-y-0.5" style={{ background: c.button, color: c.buttonText }} data-testid={`${testPrefix}-join`}>
            Rejoindre la ligue <ArrowRight size={16} />
          </Link>
        ) : <p className="flex items-center gap-2 rounded-xl p-3 text-sm" style={{ background: c.chip, color: c.light ? "#8A5A00" : "#FDE68A" }} data-testid={`${testPrefix}-not-ready`}><Hourglass size={15} /> La ligue est en préparation : l'organisateur ne l'a pas encore ouverte.</p>}
      </div>
    </div>
  );
}

export default function CorporateJoin() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get(`/e/${slug}`).then(({ data }) => setD(data)).catch((e) => setError(errMsg(e))); }, [slug]);
  if (error) return <div className="grid min-h-screen place-items-center p-6 text-center" style={{ background: "#0B0F0B", color: "#CBD5E1" }} data-testid="corp-error">{error}</div>;
  if (!d) return <Spinner />;
  const c = inviteColors(d.brand);
  return (
    <div className="relative isolate grid min-h-screen place-items-center p-5" style={{ background: c.page }} data-testid="corp-page" data-invite-bg={c.light ? "light" : "dark"}>
      <InviteBackdrop brand={d.brand} />
      <InviteCard d={d} />
    </div>
  );
}
